import { useState, useEffect, useCallback } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  GitCompare,
  Crown,
  TrendingUp,
  TrendingDown,
  Minus,
  RefreshCw,
  Loader2,
  ChevronRight,
  Gauge,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRAND_COLORS = {
  blackswan: "#DC143C",          // Crimson
  whiteswan: "#0EA5E9",          // Sky blue (dental)
  blackswan_realestate: "#f59e0b", // Amber (real estate)
  prazi: "#10b981",              // Emerald (medical)
};

const DIRECTION_COLOR_MAP = {
  emerald: "#10b981",
  green: "#84cc16",
  slate: "#94a3b8",
  amber: "#f59e0b",
  red: "#ef4444",
};

const DirectionIcon = ({ score }) => {
  if (score >= 20) return <TrendingUp className="w-5 h-5 text-emerald-500" />;
  if (score <= -20) return <TrendingDown className="w-5 h-5 text-red-500" />;
  return <Minus className="w-5 h-5 text-slate-400" />;
};

// Mini gauge for tile
const MiniGauge = ({ score, color, darkMode }) => {
  const normalized = Math.max(0, Math.min(100, (score + 100) / 2));
  const angle = -90 + (normalized * 1.8);

  return (
    <svg width="120" height="72" viewBox="0 0 120 72">
      <path
        d="M 12 60 A 48 48 0 0 1 108 60"
        fill="none"
        stroke={darkMode ? "#21262d" : "#e5e7eb"}
        strokeWidth="9"
        strokeLinecap="round"
      />
      <path
        d="M 12 60 A 48 48 0 0 1 108 60"
        fill="none"
        stroke={color}
        strokeWidth="9"
        strokeLinecap="round"
        strokeDasharray="150.8"
        strokeDashoffset={150.8 - (normalized / 100) * 150.8}
        style={{ transition: "stroke-dashoffset 0.6s ease" }}
      />
      <line
        x1="60" y1="60"
        x2={60 + 36 * Math.cos((angle * Math.PI) / 180)}
        y2={60 + 36 * Math.sin((angle * Math.PI) / 180)}
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle cx="60" cy="60" r="4" fill={color} />
      <text x="60" y="46" textAnchor="middle" fill={darkMode ? "#fff" : "#111"} fontSize="20" fontWeight="bold">
        {score > 0 ? `+${Math.round(score)}` : Math.round(score)}
      </text>
    </svg>
  );
};

export default function CrossBrandMomentum() {
  const { darkMode, region } = useOutletContext() || { darkMode: true, region: "UAE" };
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async (showToast = false) => {
    try {
      if (showToast) setRefreshing(true);
      const res = await axios.get(`${API}/algo/cross-brand-momentum?region=${region}`);
      setData(res.data);
      if (showToast) toast.success("Cross-brand momentum refreshed");
    } catch (e) {
      console.error(e);
      if (showToast) toast.error("Failed to refresh");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [region]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#DC143C]" />
      </div>
    );
  }

  const leaderboard = data?.leaderboard || [];
  const combinedHistory = data?.combined_history || [];
  const topBrand = leaderboard[0];
  const bottomBrand = leaderboard[leaderboard.length - 1];

  return (
    <div className={`p-6 space-y-6 ${darkMode ? "text-white" : "text-slate-900"}`} data-testid="cross-brand-momentum-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <GitCompare className="w-8 h-8 text-[#DC143C]" />
            Cross-Brand Momentum
          </h1>
          <p className={`mt-1 ${darkMode ? "text-slate-400" : "text-slate-600"}`}>
            Compare market momentum across all brands in {region}
          </p>
        </div>
        <Button onClick={() => fetchData(true)} disabled={refreshing} variant="outline" className="gap-2" data-testid="refresh-cross-brand-btn">
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Top movers banner */}
      {topBrand && bottomBrand && topBrand.brand !== bottomBrand.brand && (
        <div className={`grid grid-cols-1 md:grid-cols-2 gap-4`}>
          <Card className={`border-l-4 border-l-emerald-500 ${darkMode ? "bg-[#161b22] border-[#30363d]" : ""}`} data-testid="top-mover">
            <CardContent className="p-4 flex items-center gap-4">
              <Crown className="w-8 h-8 text-yellow-500 flex-shrink-0" />
              <div className="flex-1">
                <p className={`text-xs uppercase tracking-wider font-semibold ${darkMode ? "text-slate-300" : "text-slate-600"}`}>Leader</p>
                <p className={`text-lg font-bold ${darkMode ? "text-white" : "text-slate-900"}`}>{topBrand.brand_name}</p>
                <p className={`text-sm ${darkMode ? "text-slate-400" : "text-slate-500"}`}>{topBrand.direction}</p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-emerald-400">
                  {topBrand.score > 0 ? "+" : ""}{topBrand.score}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className={`border-l-4 border-l-red-500 ${darkMode ? "bg-[#161b22] border-[#30363d]" : ""}`} data-testid="bottom-mover">
            <CardContent className="p-4 flex items-center gap-4">
              <TrendingDown className="w-8 h-8 text-red-500 flex-shrink-0" />
              <div className="flex-1">
                <p className={`text-xs uppercase tracking-wider font-semibold ${darkMode ? "text-slate-300" : "text-slate-600"}`}>Trailing</p>
                <p className={`text-lg font-bold ${darkMode ? "text-white" : "text-slate-900"}`}>{bottomBrand.brand_name}</p>
                <p className={`text-sm ${darkMode ? "text-slate-400" : "text-slate-500"}`}>{bottomBrand.direction}</p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold" style={{ color: bottomBrand.score < 0 ? "#ef4444" : "#94a3b8" }}>
                  {bottomBrand.score > 0 ? "+" : ""}{bottomBrand.score}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Brand tile grid */}
      <Card className={darkMode ? "bg-[#161b22] border-[#30363d]" : ""}>
        <CardHeader>
          <CardTitle className={darkMode ? "text-white" : ""}>Brand Snapshots</CardTitle>
          <CardDescription className={darkMode ? "text-slate-400" : ""}>
            Click any tile to drill into that brand's algorithmic intelligence
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {leaderboard.map((b) => {
              const color = DIRECTION_COLOR_MAP[b.direction_color] || "#94a3b8";
              return (
                <button
                  key={b.brand}
                  onClick={() => navigate("/algorithmic")}
                  className={`p-4 rounded-lg text-left transition-all hover:scale-[1.02] ${
                    darkMode ? "bg-[#0d1117] hover:bg-[#1a1f2a] border border-[#30363d]" : "bg-slate-50 hover:bg-white border border-slate-200 shadow-sm"
                  }`}
                  data-testid={`brand-tile-${b.brand}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <p className={`font-semibold truncate ${darkMode ? "text-white" : "text-slate-900"}`}>
                        {b.brand_name}
                      </p>
                      <p className={`text-xs ${darkMode ? "text-slate-400" : "text-slate-500"}`}>
                        {b.competitor_count || 0} competitors
                      </p>
                    </div>
                    <DirectionIcon score={b.score} />
                  </div>
                  <div className="flex items-center justify-between">
                    <MiniGauge score={b.score} color={color} darkMode={darkMode} />
                    <ChevronRight className={`w-4 h-4 ${darkMode ? "text-slate-500" : "text-slate-400"}`} />
                  </div>
                  <Badge
                    variant="outline"
                    className="mt-2 text-xs"
                    style={{
                      borderColor: `${color}40`,
                      color: color,
                    }}
                  >
                    {b.direction}
                  </Badge>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Leaderboard */}
      <Card className={darkMode ? "bg-[#161b22] border-[#30363d]" : ""}>
        <CardHeader>
          <CardTitle className={`flex items-center gap-2 ${darkMode ? "text-white" : ""}`}>
            <Crown className="w-5 h-5 text-yellow-500" /> Leaderboard
          </CardTitle>
          <CardDescription className={darkMode ? "text-slate-400" : ""}>
            Brands ranked by current momentum score
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {leaderboard.map((b, i) => {
              const color = DIRECTION_COLOR_MAP[b.direction_color] || "#94a3b8";
              return (
                <div
                  key={b.brand}
                  className={`flex items-center gap-4 p-3 rounded-lg transition-colors ${
                    darkMode ? "bg-[#0d1117] hover:bg-[#1a1f2a]" : "bg-slate-50 hover:bg-slate-100"
                  }`}
                  data-testid={`leaderboard-row-${i}`}
                >
                  <div className={`w-8 text-center font-bold text-lg ${
                    i === 0 ? "text-yellow-500" : darkMode ? "text-slate-500" : "text-slate-400"
                  }`}>
                    #{i + 1}
                  </div>
                  <div className={`w-2 h-10 rounded-full`} style={{ backgroundColor: BRAND_COLORS[b.brand] || "#94a3b8" }} />
                  <div className="flex-1">
                    <p className={`font-semibold ${darkMode ? "text-white" : "text-slate-900"}`}>{b.brand_name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-xs" style={{ borderColor: `${color}40`, color }}>
                        {b.direction}
                      </Badge>
                      <span className={`text-xs ${darkMode ? "text-slate-400" : "text-slate-500"}`}>
                        {b.competitor_count || 0} competitors
                      </span>
                    </div>
                  </div>
                  {/* Component bars */}
                  <div className="hidden md:flex items-end gap-1 h-10">
                    {Object.entries(b.components || {}).map(([k, v]) => {
                      const height = Math.max(2, Math.min(40, Math.abs(v || 0)));
                      return (
                        <div
                          key={k}
                          title={`${k.replace(/_/g, " ")}: ${v}`}
                          className="w-2 rounded-sm transition-all"
                          style={{
                            height: `${height}px`,
                            backgroundColor: (v || 0) >= 0 ? "#10b981" : "#ef4444",
                            opacity: 0.8,
                          }}
                        />
                      );
                    })}
                  </div>
                  <div className="text-right w-20">
                    <p className="text-2xl font-bold" style={{ color }}>
                      {b.score > 0 ? "+" : ""}{b.score}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Combined history line chart */}
      {combinedHistory.length >= 2 && (
        <Card className={darkMode ? "bg-[#161b22] border-[#30363d]" : ""}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${darkMode ? "text-white" : ""}`}>
              <Gauge className="w-5 h-5 text-[#DC143C]" /> Momentum Trajectory
            </CardTitle>
            <CardDescription className={darkMode ? "text-slate-400" : ""}>
              Last {combinedHistory.length} data points across all brands
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={combinedHistory}>
                <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? "#30363d" : "#e5e7eb"} />
                <XAxis dataKey="date" stroke={darkMode ? "#8b949e" : "#6b7280"} fontSize={11} />
                <YAxis stroke={darkMode ? "#8b949e" : "#6b7280"} domain={[-100, 100]} />
                <Tooltip contentStyle={{ background: darkMode ? "#0d1117" : "#fff", border: "1px solid #30363d" }} />
                <Legend wrapperStyle={{ fontSize: "12px" }} />
                <ReferenceLine y={0} stroke={darkMode ? "#475569" : "#cbd5e1"} strokeDasharray="3 3" />
                {leaderboard.map((b) => (
                  <Line
                    key={b.brand}
                    type="monotone"
                    dataKey={b.brand}
                    name={b.brand_name}
                    stroke={BRAND_COLORS[b.brand] || "#94a3b8"}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Components comparison heatmap */}
      <Card className={darkMode ? "bg-[#161b22] border-[#30363d]" : ""}>
        <CardHeader>
          <CardTitle className={darkMode ? "text-white" : ""}>Component Breakdown by Brand</CardTitle>
          <CardDescription className={darkMode ? "text-slate-400" : ""}>
            Which factors drive each brand's momentum
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`border-b ${darkMode ? "border-[#30363d]" : "border-slate-200"}`}>
                  <th className={`text-left py-2 px-3 font-semibold ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Brand</th>
                  <th className={`text-right py-2 px-3 font-semibold ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Activity</th>
                  <th className={`text-right py-2 px-3 font-semibold ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Trends</th>
                  <th className={`text-right py-2 px-3 font-semibold ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Sentiment</th>
                  <th className={`text-right py-2 px-3 font-semibold ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Engagement</th>
                  <th className={`text-right py-2 px-3 font-semibold ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Intel</th>
                  <th className={`text-right py-2 px-3 font-semibold ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Total</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.map((b) => {
                  const c = b.components || {};
                  const cell = (val) => {
                    const v = val || 0;
                    let bgClass = "";
                    if (v >= 10) bgClass = "bg-emerald-500/20 text-emerald-400";
                    else if (v >= 5) bgClass = "bg-emerald-500/10 text-emerald-400";
                    else if (v <= -10) bgClass = "bg-red-500/20 text-red-400";
                    else if (v <= -5) bgClass = "bg-red-500/10 text-red-400";
                    else bgClass = darkMode ? "text-slate-400" : "text-slate-500";
                    return (
                      <td className={`text-right py-2 px-3 ${bgClass} rounded`}>
                        {v > 0 ? "+" : ""}{v}
                      </td>
                    );
                  };
                  return (
                    <tr key={b.brand} className={`border-b ${darkMode ? "border-[#21262d]" : "border-slate-100"}`}>
                      <td className={`py-2 px-3 font-medium ${darkMode ? "text-white" : "text-slate-900"}`}>{b.brand_name}</td>
                      {cell(c.activity_velocity)}
                      {cell(c.trend_volume)}
                      {cell(c.sentiment_delta)}
                      {cell(c.engagement_growth)}
                      {cell(c.intelligence_density)}
                      <td className={`text-right py-2 px-3 font-bold`} style={{ color: DIRECTION_COLOR_MAP[b.direction_color] || "#94a3b8" }}>
                        {b.score > 0 ? "+" : ""}{b.score}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
